import type { History } from "history";
import React from "react";
import type { HistoryRouterProps } from "react-router";
import { unstable_HistoryRouter as Router, Route, Routes } from "react-router";
import { RequireAuth } from "./components/RequireAuth";
import type { ServiceAuth } from "./domain/services/service-auth";
import "./elofusion/style.css";
import RedirectMobileToDashboard from "./ui/layouts/redirect-mobile-to-dashboard";
import { PageDispatcher } from "@/pages/page-dispatcher";
import { getRoutes } from "./routes";
import * as SentryReact from "@sentry/react";
import { PageDashboard } from "@/pages/page-dashboard";
import { PageOldDashboard } from "./pages/page-dashboard";
import { createGlobalBus, GlobalBusContext } from "./agv/hooks/use-global-bus";
import { configureTextBuilder } from "troika-three-text";
import { Mode } from "./api";
import { PageActionConfiguration } from "./pages/page-action-configuration";
import { useFetchPublicSettings } from "@/lib/hooks/public-setting";

const SentryRoutes = SentryReact.withSentryReactRouterV6Routing(Routes);
const globalBus = createGlobalBus();

// Disable anonymous worker scripts, as our Content-Security-Policy does not allow them.
configureTextBuilder({
    useWorker: false,
});

// Root react component.
export function Main(props: { serviceAuth: ServiceAuth; history: History }): JSX.Element {
    const [publicSettings] = useFetchPublicSettings();
    if ([...getRoutes()].length === 0) return <></>;
    // We serve one ELOfleet instance specifically for the instance dispatcher.
    // This instance shouldn't serve any other routes.
    if (window.location.hostname === "login.elofleet.com") {
        return <PageDispatcher />;
    }
    const dashboardIsLegacy = localStorage.getItem("legacyDashboard") === "true";

    return (
        // Due to newer versions of react-router not supporting the
        // way we pass in the history, we have to do some casting here.
        // See https://elokongroup.atlassian.net/jira/software/c/projects/ELF/issues/ELF-1833 for more info
        <GlobalBusContext.Provider value={globalBus}>
            <Router history={props.history as unknown as HistoryRouterProps["history"]}>
                <SentryRoutes>
                    {/* protected routes */}
                    <Route element={<RequireAuth serviceAuth={props.serviceAuth} />}>
                        {/* default route */}
                        {publicSettings?.mode === Mode.ELOSHIELD ? (
                            <Route path="/" element={<PageActionConfiguration />} />
                        ) : (
                            <Route
                                path="/"
                                element={
                                    dashboardIsLegacy ? <PageOldDashboard /> : <PageDashboard />
                                }
                            />
                        )}
                        {/* Redirect ELOfleet pages to dashboard on small screens */}
                        <Route element={<RedirectMobileToDashboard />}>
                            {/* ELOfleet routes */}
                            {[...getRoutes()]
                                .filter((route) => route.public === undefined || !route.public)
                                .map((route) => {
                                    const Component = route.component;
                                    return (
                                        <Route
                                            path={route.pattern}
                                            element={<Component />}
                                            key={route.path()}
                                        />
                                    );
                                })}
                        </Route>
                    </Route>
                    {/* public routes */}
                    {[...getRoutes()]
                        .filter((route) => route.public !== undefined && route.public)
                        .map((route) => {
                            const Component = route.component;
                            return (
                                <Route
                                    path={route.pattern}
                                    element={<Component />}
                                    key={route.path()}
                                />
                            );
                        })}
                </SentryRoutes>
            </Router>
        </GlobalBusContext.Provider>
    );
}
